/**
 * DriverOnboardingWizard — first-login profile completion gate
 * Extracted from DriverApp.jsx (V2-03.12k)
 * Reuses same logic as manager portal onboarding wizard.
 */

import React, { useState } from 'react';
import { C, FONT, GLASS, GLASS_STRONG, INPUT, BTN, BTN_DISABLED } from '../driver-tokens.js';
import { apiPost } from '../auth/driver-session.js';
import type { DriverEmployee } from '../DriverApp.js';

interface OnboardingWizardProps {
  employee: DriverEmployee;
  onComplete: () => void;
}

function DriverOnboardingWizard({ employee, onComplete }: OnboardingWizardProps) {
  var [step, setStep] = useState(0);
  var [form, setForm] = useState<Record<string, string>>({
    nickname: "", phone: employee.phone || "", emergency_contact_name: "", emergency_contact_phone: "",
    emergency_contact_relationship: "", address_line1: "", address_line2: "", address_city: "", address_state: "",
    address_zip: "", dob: "", dl_number: "", shirt_size: "", pants_size: "", jacket_size: "", photo_url: ""
  });
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState<string | null>(null);

  var steps = [
    { title: "Photo", required: [] },
    { title: "Identity", required: ["phone"] },
    { title: "Emergency Contact", required: ["emergency_contact_name", "emergency_contact_phone"] },
    { title: "Address & ID", required: [] },
    { title: "Uniforms", required: [] },
    { title: "Review", required: [] }
  ];

  function updateField(f: string, v: string) { setForm(function(p) { return { ...p, [f]: v }; }); }
  function canProceed() { return steps[step].required.every(function(f) { return !!form[f]; }); }

  function handleSubmit() {
    setSaving(true);
    apiPost<{ success?: boolean; profile_completed?: boolean; error?: string }>("/api/directory/onboarding", form).then(function(data) {
      setSaving(false);
      if (data.success || data.profile_completed) { onComplete(); }
      else { setError(data.error || "Failed"); }
    });
  }

  function renderInput(label: string, field: string, opts?: { required?: boolean; type?: string }) {
    opts = opts || {};
    return React.createElement("div", { style: { marginBottom: 14 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, label, opts.required ? " *" : ""),
      React.createElement("input", {
        type: opts.type || "text",
        style: { ...INPUT, borderColor: opts.required && !form[field] ? C.red : "rgba(255,255,255,0.08)" },
        value: form[field] || "",
        onChange: function(e) { updateField(field, e.target.value); }
      })
    );
  }

  function renderSelect(label: string, field: string, options: string[]) {
    return React.createElement("div", { style: { marginBottom: 14 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, label),
      React.createElement("select", { style: { ...INPUT, appearance: "none" }, value: form[field] || "", onChange: function(e: React.ChangeEvent<HTMLSelectElement>) { updateField(field, e.target.value); } },
        React.createElement("option", { value: "" }, "Select..."),
        options.map(function(o) { return React.createElement("option", { key: o, value: o }, o); })
      )
    );
  }

  var content;
  switch (step) {
    case 0: content = React.createElement("div", { style: { textAlign: "center", padding: 20 } },
      React.createElement("div", { style: { width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.03)", border: "3px dashed rgba(255,255,255,0.1)", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" } },
        React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.textMuted } }, "Photo")
      ),
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted } }, "You can add a photo later")
    ); break;
    case 1: content = React.createElement("div", null,
      React.createElement("div", { style: { ...GLASS, padding: 12, marginBottom: 16 } },
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 14, color: C.text } }, employee.first_name + " " + employee.last_name),
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted } }, "ID: " + employee.driver_id)
      ),
      renderInput("Nickname", "nickname"),
      renderInput("Phone", "phone", { required: true })
    ); break;
    case 2: content = React.createElement("div", null,
      renderInput("Contact Name", "emergency_contact_name", { required: true }),
      renderInput("Contact Phone", "emergency_contact_phone", { required: true }),
      renderInput("Relationship", "emergency_contact_relationship")
    ); break;
    case 3: content = React.createElement("div", null,
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted, marginBottom: 10, padding: 8, background: "rgba(255,255,255,0.02)", borderRadius: 8 } }, "Stored securely. Only visible to HR."),
      renderInput("Address", "address_line1"),
      renderInput("Apt/Suite", "address_line2"),
      renderInput("City", "address_city"),
      renderInput("State", "address_state"),
      renderInput("ZIP", "address_zip"),
      renderInput("Date of Birth", "dob", { type: "date" }),
      renderInput("Driver License #", "dl_number")
    ); break;
    case 4: content = React.createElement("div", null,
      renderSelect("Shirt Size", "shirt_size", ["XS","S","M","L","XL","2XL","3XL","4XL"]),
      renderInput("Pants Size", "pants_size"),
      renderSelect("Jacket Size", "jacket_size", ["XS","S","M","L","XL","2XL","3XL","4XL"])
    ); break;
    case 5: content = React.createElement("div", null,
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.silver, marginBottom: 12 } }, "Review your info. Tap to edit."),
      ["phone","emergency_contact_name","emergency_contact_phone","address_line1","address_city","shirt_size","pants_size","jacket_size"].map(function(f) {
        return React.createElement("div", { key: f, style: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" } },
          React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase" } }, f.replace(/_/g, " ")),
          React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 13, color: form[f] ? C.text : C.textMuted } }, form[f] || "\u2014")
        );
      })
    ); break;
  }

  var progress = ((step + 1) / steps.length) * 100;

  return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: C.dark } },
    React.createElement("div", { style: { ...GLASS_STRONG, maxWidth: 420, width: "100%", padding: 28, borderRadius: 20 } },
      React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 22, color: C.text, textAlign: "center", marginBottom: 4 } }, "Welcome to PWD"),
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver, textAlign: "center", marginBottom: 20 } }, "Step " + (step+1) + "/" + steps.length + " \u2014 " + steps[step].title),
      React.createElement("div", { style: { height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginBottom: 20 } },
        React.createElement("div", { style: { height: "100%", width: progress + "%", background: C.gold, borderRadius: 2, transition: "width 0.3s" } })
      ),
      React.createElement("div", { style: { minHeight: 200, marginBottom: 20 } }, content),
      error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: "center", marginBottom: 12 } }, error),
      React.createElement("div", { style: { display: "flex", justifyContent: "space-between" } },
        step > 0 ? React.createElement("button", { style: { ...GLASS, padding: "12px 20px", color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: "pointer", minHeight: 48 }, onClick: function() { setStep(step-1); } }, "\u2190") : React.createElement("div"),
        step < 5 ? React.createElement("button", { style: canProceed() ? BTN : BTN_DISABLED, disabled: !canProceed(), onClick: function() { setStep(step+1); } }, "Next \u2192")
          : React.createElement("button", { style: saving ? BTN_DISABLED : BTN, disabled: saving, onClick: handleSubmit }, saving ? "Saving..." : "Complete")
      )
    )
  );
}

export default DriverOnboardingWizard;
